import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

const read = <T>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);

    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    // Storage blocked (a private window) or a value that isn't JSON: start from the fallback.
    return fallback;
  }
};

/**
 * SW2-2533 — `useState` kept in this browser's localStorage under `key` (as JSON), so it survives
 * leaving the page and reloading. Functional updates see the latest value, as with `useState`:
 * react-use's `useLocalStorage` hands them the value from its first render, so two changes in a row
 * lost the first. A different `key` (another user) reads that key's value. Storage failures are
 * ignored: the state still works for the visit.
 */
export default function useStoredState<T>(key: string, fallback: T): [T, Dispatch<SetStateAction<T>>] {
  const fallbackRef = useRef(fallback);
  const [state, setState] = useState<{ key: string; value: T }>(() => ({ key, value: read(key, fallback) }));
  // Another key: its own saved value, from this render on.
  const value = state.key === key ? state.value : read(key, fallbackRef.current);

  useEffect(() => {
    if (state.key !== key) setState({ key, value: read(key, fallbackRef.current) });
  }, [key, state.key]);

  useEffect(() => {
    if (state.key !== key) return;
    try {
      localStorage.setItem(key, JSON.stringify(state.value));
    } catch {
      // Full or blocked: keep working without saving.
    }
  }, [key, state]);

  // Stable for a key, so callers can depend on it.
  const setValue: Dispatch<SetStateAction<T>> = useCallback(
    (next) =>
      setState((current) => {
        const base = current.key === key ? current.value : read(key, fallbackRef.current);

        return { key, value: typeof next === 'function' ? (next as (prev: T) => T)(base) : next };
      }),
    [key],
  );

  return [value, setValue];
}
